import classnames from 'classnames'
import React, { memo, useCallback, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { ProductCardTag } from '@src/components/product-card/product-card.types'
import { ProductTags } from '@src/components/product-card/product-image-section/product-tags'
import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { useStatsigExperiment } from '@src/experiments/statsig-integration/useStatsigExperiment'
import Directory from '@src/quince-site-common/src/domains/Directory'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import usePDPModelSizeTagExperiment from '@src/quince-site-common/src/hooks/usePDPModelSizeTagExperiment'
import usePDPTagVisibility from '@src/quince-site-common/src/hooks/usePDPTagVisibility'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { TAG_LOCATION, TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { getVideoInfoForColor } from '@src/quince-site-pdp/src/molecules/productFormWrapper/utils'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

import { BreadCrumb } from '../breadCrumb'
import Icon from '../icon/Icon'
import { SliderImages, SliderImagesProps } from '../sliderImages'

import styles from './sliderImageText.module.scss'
import stylesV2 from './sliderImageTextV2.module.scss'

export interface SliderImageWithTextProps {
  className?: string
  // This component owns the popup state and injects it below, so callers must not pass it.
  sliderImageProps: Omit<SliderImagesProps, 'showImagePopup' | 'setShowImagePopup'>
  isWishListed?: boolean
  hideWishList?: boolean
  productTags?: ProductTag[]
  onWishListToggle?: (e: React.MouseEvent<HTMLElement | SVGElement>) => void
  directory: Directory
  getTopLeftIconSVG?: ({ index }: { index: number }) => ((params: any) => JSX.Element) | null
  hideBreadCrumb?: boolean
  /**
   * When `hideBreadCrumb` is true, `SliderImageWithText` normally still renders a `4.8rem`
   * placeholder div (`container_hideBreadCrumb`) inside its sticky header to reserve the
   * vertical space where the breadcrumb would have been — non-PL's `PDP_BREADCRUMB=GROUP_B/C`
   * experiment uses this so the wishlist heart floats over the image without the image jumping
   * up. Callers that render their own breadcrumb OUTSIDE this component (PL's PDP renders a
   * standalone BREADCRUMBS widget above the gallery) can pass `hideBreadCrumbSpacer` to also
   * skip the placeholder — otherwise the two would stack and produce a `9.6rem` visible gap.
   * Defaults false → non-PL behaviour unchanged.
   */
  hideBreadCrumbSpacer?: boolean
  breadCrumbListClass?: string
  breadCrumbContainerHeaderClass?: string
  variants?: any[]
  product?: Product
  mediaTags?: (ProductCardTag | null)[]
  isBundle?: boolean
  selectedOptions?: ProductVariantOption[]
}

const SliderImageWithText = ({
  className,
  sliderImageProps,
  isWishListed,
  onWishListToggle,
  productTags,
  directory,
  hideWishList,
  getTopLeftIconSVG,
  hideBreadCrumb,
  hideBreadCrumbSpacer,
  breadCrumbListClass,
  breadCrumbContainerHeaderClass,
  variants,
  product,
  isBundle,
  mediaTags,
  selectedOptions,
}: SliderImageWithTextProps) => {
  const deviceInfo = useDeviceInfo()
  const { isTagVisible } = usePDPTagVisibility()
  const [showImagePopup, setShowImagePopup] = useState(false)
  const finalStyles = stylesV2
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  const breadCrumbExp = useStatsigExperiment(STATSIG_EXPERIMENTS.PDP_BREADCRUMB)
  const breadCrumbBucket = breadCrumbExp?.experimentBucket
  const isBreadCrumbGroupC = breadCrumbBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_C
  const isBreadCrumbGroupB = breadCrumbBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_B
  const showWishlistOnImage = isBreadCrumbGroupB || isBreadCrumbGroupC
  const hideBreadCrumbComponent = hideBreadCrumb ?? showWishlistOnImage

  const classes = classnames(finalStyles.container, className, {
    [finalStyles.container_scrollingDown as string]: isScrollingDown,
    [finalStyles.container_groupCOffset as string]: isBreadCrumbGroupC,
    [finalStyles.container_addDivider as string]: isBundle,
    [finalStyles.container_pdpTagVisible as string]: isTagVisible,
    [finalStyles.container_isImagePopupOpen as string]: showImagePopup,
  })
  const showModelSizeTag = usePDPModelSizeTagExperiment()
  const tagsToShowOnPdp = productTags?.filter(tag => tag.showOnProductPage)
  const STYLE_WHITE = 'white'
  const STYLE_BLACK = 'black'
  const innerStyle = {
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
  }
  const isModelSizeVisibleOnMobile = showModelSizeTag && isMobile && mediaTags?.some(t => !!t)
  const productTagsNewClass = isModelSizeVisibleOnMobile

  const renderMainImageOverlay = useCallback(
    (index: number) => {
      const topLeftIconSVG = getTopLeftIconSVG?.({ index })
      const mediaTag = mediaTags?.[index] ? [mediaTags[index]] : []
      return (
        <div className={finalStyles.imageOverlay}>
          {topLeftIconSVG ? (
            <div className={finalStyles.awardIcon}>
              <Icon iconSvg={topLeftIconSVG} className={finalStyles.iconStyles} />
            </div>
          ) : null}
          {productTags && (isTagVisible || Boolean(tagsToShowOnPdp?.length)) && !isMobile && (
            <ProductTags
              applicableTags={isTagVisible && !isBundle ? productTags : tagsToShowOnPdp}
              position={isMobile ? TAG_POSITION.BOTTOM : TAG_POSITION.TOP}
              tagLocation={TAG_LOCATION.PDP}
            />
          )}
          {showModelSizeTag && !isMobile && mediaTag?.length ? (
            <ProductTags
              applicableTags={mediaTag}
              position={TAG_POSITION.BOTTOM}
              sticky={true}
              rounded={true}
              transformToSentenceCase={false}
              isModelSizeTag={true}
            />
          ) : null}
        </div>
      )
    },
    [productTags, getTopLeftIconSVG, isMobile, isTagVisible, showModelSizeTag, mediaTags],
  )

  const renderMediaInfoText = useCallback(
    (index: number) => {
      const mediaTag = mediaTags?.[index] ? [mediaTags[index]] : []
      const anyMediaHasModelSizeTags = mediaTags?.some(t => !!t)

      return showModelSizeTag && anyMediaHasModelSizeTags ? (
        <div className={classnames(styles.modelSizeInfo, styles.modelSizeInfo_newDesignV2)}>{mediaTag[0]?.value}</div>
      ) : null
    },
    [mediaTags, showModelSizeTag],
  )

  const currentColor = selectedOptions?.find(opt => opt.name === ProductOptionName.Color)?.value ?? null
  const videoProperties = getVideoInfoForColor({ product, currentColor })
  const selectedVariant =
    product && selectedOptions?.length ? productHelper.getVariantsForOptions(product, selectedOptions)[0] : undefined
  const videoTrackingProperties = {
    productProperties: product
      ? getProductPropertiesForTracking(product, selectedVariant?.svid?.toString())
      : undefined,
    variant_id: selectedVariant?.svid ?? undefined,
    video_id: videoProperties?.videoId ?? undefined,
    video_format: videoProperties?.videoFormat ?? undefined,
    video_position: videoProperties?.videoPosition ?? undefined,
    autoplayed: videoProperties?.videoId ? 1 : 0,
  }

  return (
    <div style={{ position: 'relative' }}>
      <div className={classes}>
        {!hideBreadCrumbComponent ? (
          <div
            className={classnames(
              finalStyles.container__header,
              breadCrumbContainerHeaderClass,
              finalStyles.container__header_adjustedPadding,
            )}
          >
            <div className={finalStyles.container__header__text}>
              <BreadCrumb
                directory={directory}
                breadCrumbListClass={breadCrumbListClass}
                deviceType={deviceInfo.deviceType}
                isPDP={true}
              />
            </div>
          </div>
        ) : hideBreadCrumbSpacer ? null : (
          <div
            className={
              isBreadCrumbGroupC ? finalStyles.container_hideBreadCrumbGroupC : finalStyles.container_hideBreadCrumb
            }
          />
        )}
        {(isTagVisible || Boolean(tagsToShowOnPdp?.length)) && isMobile && (
          <ProductTags
            applicableTags={isTagVisible && !isBundle ? productTags : tagsToShowOnPdp}
            position={isMobile ? TAG_POSITION.BOTTOM : TAG_POSITION.TOP}
            tagLocation={TAG_LOCATION.PDP}
            productTagsNewClass={productTagsNewClass}
            isModelSizeVisibleOnMobile={isModelSizeVisibleOnMobile}
          />
        )}

        <SliderImages
          {...sliderImageProps}
          renderMainImageOverlay={renderMainImageOverlay}
          variants={variants}
          product={product}
          videoTrackingProperties={videoTrackingProperties}
          isWishListed={isWishListed}
          onWishListToggle={onWishListToggle}
          innerStyle={innerStyle}
          hideWishList={hideWishList}
          renderMediaInfoText={renderMediaInfoText}
          isBundle={isBundle}
          showImagePopup={showImagePopup}
          setShowImagePopup={setShowImagePopup}
          showWishlistOnImage={showWishlistOnImage}
        />
      </div>
    </div>
  )
}

export default memo(SliderImageWithText)
